import { FC } from "react";
import wrapperHOC from "@Utils/wrapperHOC";

import styles from "./HomeV2SearchAiIcon.module.scss";

/** Centre of the sparkle in the source artwork — the point the minis are cloned from. */
const SPARK_CX = 15.5;
const SPARK_CY = 7;
const SPARK_PATH =
  "M15.5 3.50003L15.7579 4.19706C16.0961 5.11105 16.2652 5.56805 16.5986 5.90142C16.932 6.2348 17.389 6.4039 18.303 6.74211L19 7.00003L18.303 7.25795C17.389 7.59616 16.932 7.76527 16.5986 8.09864C16.2652 8.43201 16.0961 8.88901 15.7579 9.803L15.5 10.5L15.2421 9.803C14.9039 8.88901 14.7348 8.43201 14.4014 8.09864C14.068 7.76527 13.611 7.59616 12.697 7.25795L12 7.00003L12.697 6.74211C13.611 6.4039 14.068 6.2348 14.4014 5.90142C14.7348 5.56805 14.9039 5.11105 15.2421 4.19706L15.5 3.50003Z";

/**
 * The small sparks that pop around the lens mid-pass, as in the Lottie this replaces.
 * Each is a scaled clone of the main sparkle, so the stroke is restated at 1.5 / scale
 * to keep every line the same visual weight.
 */
const MINIS = [
  { x: 20.5, y: 12.5, s: 0.38, cls: "mini1" },
  { x: 6.5, y: 4, s: 0.32, cls: "mini2" },
  { x: 4.5, y: 19.5, s: 0.36, cls: "mini3" },
] as const;

interface Props {
  /** Runs one pass each time this goes false → true. */
  playing: boolean;
  className?: string;
}

/**
 * The search CTA's "AI search" mark (Hugeicons ai-search-02, stroke-rounded), animated
 * in CSS rather than as a Lottie: the artwork is three paths, so keyframes on them are
 * lighter than shipping lottie-web for it, and it renders in the SSR HTML.
 */
const HomeV2SearchAiIcon: FC<Props> = ({ playing, className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={[styles.icon, playing ? styles.playing : "", className || ""].join(" ")}
  >
    <g className={styles.lens}>
      <path d="M16.0001 16.5L20 20.5" />
      <path d="M18 11.5C18 15.366 14.866 18.5 11 18.5C7.13401 18.5 4 15.366 4 11.5C4 7.63404 7.13401 4.50003 11 4.50003" />
    </g>
    <path className={styles.spark} d={SPARK_PATH} />
    {MINIS.map(({ x, y, s, cls }) => (
      <g
        key={cls}
        transform={`translate(${x} ${y}) scale(${s}) translate(${-SPARK_CX} ${-SPARK_CY})`}
      >
        <path className={`${styles.mini} ${styles[cls]}`} d={SPARK_PATH} strokeWidth={1.5 / s} />
      </g>
    ))}
  </svg>
);

export default wrapperHOC(HomeV2SearchAiIcon, {
  componentName: "SearchAiIcon-HomeV2",
  showForChina: true,
});
